import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enums ────────────────────────────────────────────────────────────────
export const FacilityType = {
  PADEL: 'PADEL',
  BASKETBALL: 'BASKETBALL',
  TENNIS: 'TENNIS',
  GYM: 'GYM',
  POOL: 'POOL',
  MEETING_ROOM: 'MEETING_ROOM',
  BBQ_AREA: 'BBQ_AREA',
  OTHER: 'OTHER',
} as const;
export type FacilityType = (typeof FacilityType)[keyof typeof FacilityType];

export const BookingStatus = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
} as const;
export type BookingStatus = (typeof BookingStatus)[keyof typeof BookingStatus];

const FACILITY_TYPES = [
  FacilityType.PADEL,
  FacilityType.BASKETBALL,
  FacilityType.TENNIS,
  FacilityType.GYM,
  FacilityType.POOL,
  FacilityType.MEETING_ROOM,
  FacilityType.BBQ_AREA,
  FacilityType.OTHER,
] as const;

const BOOKING_STATUSES = [
  BookingStatus.PENDING_APPROVAL,
  BookingStatus.CONFIRMED,
  BookingStatus.CANCELLED,
  BookingStatus.REJECTED,
  BookingStatus.COMPLETED,
] as const;

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const hhmm = z.string().regex(/^\d{2}:\d{2}$/, 'Expected HH:MM');

// ─── Public shapes ────────────────────────────────────────────────────────
export const publicFacility = z.object({
  id: uuid,
  cluster_id: uuid,
  name: z.string(),
  type: z.enum(FACILITY_TYPES),
  description: z.string().nullable(),
  capacity: z.number().int().nullable(),
  open_time: z.string().nullable(),       // HH:MM or null
  close_time: z.string().nullable(),
  requires_booking: z.boolean(),          // false = walk-in, no slots/bookings
  slot_minutes: z.number().int().positive(),
  requires_approval: z.boolean(),
  image_url: z.string().nullable(),
  is_active: z.boolean(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
});
export type PublicFacility = z.infer<typeof publicFacility>;

export const publicBooking = z.object({
  id: uuid,
  cluster_id: uuid,
  facility_id: uuid,
  user_id: uuid,
  unit_id: uuid,
  start_at: isoDatetime,
  end_at: isoDatetime,
  status: z.enum(BOOKING_STATUSES),
  notes: z.string().nullable(),
  approved_at: isoDatetime.nullable(),
  rejected_at: isoDatetime.nullable(),
  rejected_reason: z.string().nullable(),
  cancelled_at: isoDatetime.nullable(),
  cancelled_reason: z.string().nullable(),
  created_at: isoDatetime,
  // Joined views often include the facility for display
  facility: publicFacility.optional(),
});
export type PublicBooking = z.infer<typeof publicBooking>;

// ─── Resident queries ─────────────────────────────────────────────────────
export const listFacilitiesQuery = paginationQuery.extend({
  type: z.enum(FACILITY_TYPES).optional(),
});
export type ListFacilitiesQuery = z.infer<typeof listFacilitiesQuery>;

export const facilityAvailabilityQuery = z.object({
  date: dateStr,
});
export type FacilityAvailabilityQuery = z.infer<typeof facilityAvailabilityQuery>;

export const createBookingRequest = z.object({
  facility_id: uuid,
  start_at: isoDatetime,
  end_at: isoDatetime,
  notes: z.string().max(500).optional(),
});
export type CreateBookingRequest = z.infer<typeof createBookingRequest>;

export const listBookingsQuery = paginationQuery.extend({
  status: z.enum(BOOKING_STATUSES).optional(),
  facility_id: uuid.optional(),
  since: isoDatetime.optional(),
});
export type ListBookingsQuery = z.infer<typeof listBookingsQuery>;

export const cancelBookingRequest = z.object({
  reason: z.string().max(200).optional(),
});

// ─── Admin requests ───────────────────────────────────────────────────────
export const createFacilityRequest = z.object({
  name: z.string().min(1).max(100).trim(),
  type: z.enum(FACILITY_TYPES).default(FacilityType.OTHER),
  description: z.string().max(1000).optional(),
  capacity: z.number().int().positive().optional(),
  open_time: hhmm.optional(),
  close_time: hhmm.optional(),
  // Default true = bookable, so existing callers and current behavior are
  // unchanged. Set false for walk-in facilities.
  requires_booking: z.boolean().default(true),
  slot_minutes: z.number().int().positive().default(60),
  requires_approval: z.boolean().default(false),
  // Accepts an https URL (R2) or a base64 data-URL fallback (when R2 isn't
  // provisioned). Capped so a ~3 MB image stored as a data URL stays within
  // D1's row limits — base64 is ~1.34x raw, so 3 MB → ~4.2M chars.
  image_url: z.string().min(1).max(4_300_000).optional(),
});
export type CreateFacilityRequest = z.infer<typeof createFacilityRequest>;

export const updateFacilityRequest = createFacilityRequest.partial().extend({
  is_active: z.boolean().optional(),
});
export type UpdateFacilityRequest = z.infer<typeof updateFacilityRequest>;

export const rejectBookingRequest = z.object({
  reason: z.string().min(1).max(200).trim(),
});
